import React from 'react';
import { Box, Typography, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  statusPanel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(2),
  },
  statusRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  compactContainer: {
    display: 'flex',
    alignItems: 'center',
    '& > *': {
      marginRight: theme.spacing(1),
    },
    '& > *:last-child': {
      marginRight: 0,
    },
  },
}));

const getStatusColor = (status, type) => {
  if (type === 'reporting') {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'INACTIVE': return 'warning';
      case 'EXPIRED': return 'error';
      case 'BLOCKED': return 'error';
      default: return 'default';
    }
  }
  
  if (type === 'tradelines') {
    if (status === 0) return 'error';
    if (status === 3) return 'success';
    return 'warning';
  }
  
  if (type === 'case') {
    switch (status) {
      case 'pending_opt_in': return 'warning';
      case 'pending_opt_out': return 'warning';
      case 'submitted': return 'success';
      case 'closed': return 'error';
      default: return 'default';
    }
  }
  
  if (type === 'primary') {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'finished': return 'info';
      case 'opted_out': return 'error';
      default: return 'default';
    }
  }
  
  return 'default';
};

const getReportingStatus = (enrollment) => {
  const { status, moved_out_date } = enrollment;
  const caseStatus = enrollment.case?.status;
  
  // Check for opt-out from status
  if (status === 'opted_out') {
    return 'OPTED_OUT';
  }
  
  if (caseStatus === 'closed') {
    return 'OPTED_OUT';
  }
  
  if (moved_out_date) {
    return 'MOVED_OUT';
  }
  
  if (caseStatus === 'pending_opt_in') {
    return 'BLOCKED';
  }
  
  if (caseStatus === 'pending_opt_out') {
    return 'BLOCKED';
  }
  
  if (status === 'approved') {
    // Without backend data, assume approved means potentially active
    return 'ACTIVE';
  }
  
  if (status === 'finished') {
    return 'FINISHED';
  }
  
  if (status === 'rejected') {
    return 'REJECTED';
  }
  
  return 'INACTIVE';
};

const formatCaseStatus = (caseStatus) => {
  if (!caseStatus) return 'unknown';
  
  const statusMap = {
    'pending_opt_in': 'Pending Opt-In',
    'pending_opt_out': 'Pending Opt-Out',
    'not_started': 'Not Started',
    'started': 'Started',
    'submitted': 'Submitted',
    'closed': 'Closed'
  };
  
  return statusMap[caseStatus] || caseStatus;
};

const EnhancedStatusDisplay = ({ enrollment, variant = 'full' }) => {
  const classes = useStyles();
  const caseStatus = enrollment.caseStatus || enrollment.case?.status;
  const reportingAccountStatus = enrollment.reportingAccountStatus;
  const partnerActive = enrollment.partnerCustomerActive;
  
  if (variant === 'compact') {
    return (
      <Box className={classes.compactContainer}>
        <Chip 
          label={enrollment.status?.toUpperCase() || 'UNKNOWN'} 
          color={getStatusColor(enrollment.status, 'primary')}
          size="small"
        />
        {caseStatus && (
          <Chip 
            label={`Case: ${formatCaseStatus(caseStatus)}`} 
            color={getStatusColor(caseStatus, 'case')}
            size="small"
          />
        )}
        {reportingAccountStatus && (
          <Chip 
            label={`RA: ${reportingAccountStatus.toUpperCase()}`} 
            color={getStatusColor(reportingAccountStatus, 'reporting')}
            size="small"
          />
        )}
        {enrollment.fromPartner && partnerActive === false && (
          <Chip 
            label="Partner Inactive" 
            color="error"
            size="small"
          />
        )}
      </Box>
    );
  }
  
  return (
    <Box className={classes.statusPanel}>
      <Typography variant="h6" style={{ marginBottom: 16, fontWeight: 'bold' }}>
        Status Overview
      </Typography>
      
      <Box className={classes.statusRow}>
        <Typography variant="body2" style={{ fontWeight: 500 }}>
          Enrollment:
        </Typography>
        <Chip 
          label={enrollment.status?.toUpperCase() || 'UNKNOWN'} 
          color={getStatusColor(enrollment.status, 'primary')}
          size="small"
        />
      </Box>
      
      {caseStatus && (
        <Box className={classes.statusRow}>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            Case:
          </Typography>
          <Chip 
            label={formatCaseStatus(caseStatus)} 
            color={getStatusColor(caseStatus, 'case')}
            size="small"
          />
        </Box>
      )}
      
      {reportingAccountStatus && (
        <Box className={classes.statusRow}>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            Reporting Account:
          </Typography>
          <Chip 
            label={reportingAccountStatus.toUpperCase()} 
            color={getStatusColor(reportingAccountStatus, 'reporting')}
            size="small"
          />
        </Box>
      )}
      
      {enrollment.fromPartner && (
        <Box className={classes.statusRow}>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            Partner Relationship:
          </Typography>
          <Chip 
            label={partnerActive ? 'ACTIVE' : 'INACTIVE'} 
            color={partnerActive ? 'success' : 'error'}
            size="small"
          />
        </Box>
      )}
      
      {enrollment.moved_out_date && (
        <Box className={classes.statusRow}>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            Moved Out:
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {new Date(enrollment.moved_out_date).toLocaleDateString()}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EnhancedStatusDisplay;